import { VoucherState, VoucherType } from '../../../types'
import { Link } from 'react-router-dom'
import { FaCaretDown, FaCaretRight } from 'react-icons/fa'
import { GenericColumnDef } from '../DataTable'
import VoucherStateBadge from '../VoucherStateBadge'
import VoucherCommentsBadge from '../VoucherCommentsBadge'
import PaymentAccountBadges from '../PaymentAccountBadges'
import {
  getLatestApprovingUser,
  formatWaitingSince,
  getWaitingSinceValue,
} from '../../../utils/utils'
import Tooltip from '@mui/material/Tooltip'
import Chip from '@mui/material/Chip'
import theme from '../../../theme'
import TagChips from '../../Tags/TagChips'

export const getInboxColumns = (): GenericColumnDef<VoucherType>[] => [
  {
    id: 'expander',
    header: () => null,
    cell: ({ row }) =>
      row.getCanExpand() ? (
        <button
          onClick={row.getToggleExpandedHandler()}
          style={{ cursor: 'pointer', border: 'none', background: 'none' }}
        >
          {row.getIsExpanded() ? (
            <FaCaretDown color={theme.palette.primary.main} />
          ) : (
            <FaCaretRight color={theme.palette.primary.main} />
          )}
        </button>
      ) : null,
    size: 40,
  },
  {
    accessorKey: 'reference_number',
    header: 'Reference #',
    cell: ({ row }) => {
      const isWaitingOnRequester =
        (row.original.state === VoucherState.SUBMITTED ||
          row.original.state === VoucherState.IN_PROGRESS) &&
        row.original.submitted_at !== null &&
        (row.original.total_amount === 0 ||
          row.original.voucher_cost_category.length === 0)

      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <PaymentAccountBadges voucher={row.original} />
          <Link to={`/panel/inbox/${row.original.id}/edit`}>
            {row.original.reference_number ?? 'Draft'}
          </Link>
          <VoucherCommentsBadge voucher={row.original} />
          {isWaitingOnRequester && (
            <Tooltip title="Missing an amount or cost category — the requester needs to finish it before you can act">
              <Chip
                label="Waiting on requester"
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.68rem' }}
              />
            </Tooltip>
          )}
        </div>
      )
    },
    filterFn: 'includesString',
    size: 20,
  },
  { accessorFn: (row) => row.department.name, header: 'Department', size: 20 },
  {
    accessorFn: (row) =>
      row.submitted_at
        ? new Date((row.submitted_at as number) * 1).toISOString().split('T')[0]
        : '',
    header: 'Submitted At',
    size: 80,
  },
  {
    accessorFn: (row) =>
      row.created_by.first_name + ' ' + row.created_by.last_name,
    header: 'Prepared by',
    size: 200,
  },
  {
    accessorKey: 'state',
    header: 'State',
    cell: ({ row }) => <VoucherStateBadge state={row.original.state} />,
    size: 10,
  },
  {
    id: 'tags',
    header: 'Tags',
    cell: ({ row }) => <TagChips tags={row.original.tags} />,
    size: 120,
  },
  {
    id: 'waiting_since',
    accessorFn: (row) => formatWaitingSince(getWaitingSinceValue(row)),
    header: () => (
      <Tooltip title="How long this voucher has been waiting for your action — since it was submitted, or since it last moved forward to reach you">
        <span>Waiting since</span>
      </Tooltip>
    ),
    enableColumnFilter: false,
    size: 100,
  },
  {
    accessorFn: (row: VoucherType) => {
      const approver = getLatestApprovingUser(row)
      return approver ? `${approver.first_name} ${approver.last_name}` : ''
    },
    header: 'Last Approval',
  },
  {
    accessorFn: (row) =>
      new Intl.NumberFormat('en-SE', {
        style: 'currency',
        currency: row.currency,
      }).format(row.total_amount),
    header: 'Amount',
    enableColumnFilter: false,
    meta: { align: 'right' },
    size: 120,
  },
]
